import { Lightbulb } from "lucide-react";
import Select from "react-select";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { GetCoachingProgramsQuery } from "@/graphql";
import { SelectFn, SelectOption } from "@/types/select";
import { formatDate } from "@/utils/datetime";

import { findFirstAvailableSession } from "./find-first-available-session";

type Program = GetCoachingProgramsQuery["coachingPrograms"]["programs"][number];
type Session = Program["sessions"][number];
type Product = Session["product"];
type CoachProduct = Program["coaches"][number]["coachProduct"];

const CUSTOM_SELECT_STYLES = {
  option: (provided: ExplicitAny, state: ExplicitAny) => ({
    ...provided,
    color: state.isDisabled ? "var(--color-gray-500)" : provided.color,
    backgroundColor: state.isDisabled
      ? state.isFocused
        ? "var(--color-gray-100)"
        : "white"
      : provided.backgroundColor,
    cursor: state.isDisabled ? "not-allowed" : "pointer",
  }),
};

interface ProgramCardProps {
  programs: Program[];
  selectedProgramId: string | null;
  selectedSession: Session | null;
  selectedProduct: Product | null;
  onSelectProgramId: (programId: string) => void;
  onSelectSession: (session: Session | null) => void;
  onSelectProduct: (
    product: Product | null,
    coachProduct: CoachProduct | null,
  ) => void;
  tab: "program" | "independent";
  onTabChange: (tab: "program" | "independent") => void;
}

export function ProgramCard({
  programs,
  selectedProgramId,
  selectedSession,
  selectedProduct,
  onSelectProgramId,
  onSelectSession,
  onSelectProduct,
  tab,
  onTabChange,
}: ProgramCardProps) {
  const selectedProgram = programs.find(
    (p) => p.programId === selectedProgramId,
  );
  const sessions = selectedProgram?.sessions || [];

  const programOptions: SelectOption[] = programs.map((p) => {
    const label =
      p.programName +
      (p.isCoachInProgram ? "" : " (Coach not available)") +
      (p.areAllSessionsBooked ? " (All sessions booked)" : "");
    const disabled = !p.isCoachInProgram || p.areAllSessionsBooked;
    return {
      value: p.programId,
      label,
      disabled,
    };
  });

  const sessionOptions: SelectOption[] = sessions.map((s) => {
    let statusText = "";
    const coach = selectedProgram?.coaches.find((c) => c.sessionId === s.id);
    if (coach?.isBooked) {
      statusText = " (Already booked)";
    } else if (!coach?.isAvailable) {
      statusText = " (Coach not available)";
    } else if (coach?.requiresSelection) {
      statusText = " (Coach not selected in program)";
    }

    return {
      value: s.id,
      label: (s.name || "") + statusText,
      disabled:
        !coach?.isAvailable || coach?.isBooked || coach?.requiresSelection,
    };
  });

  const onSelectSessionOption: SelectFn<SelectOption> = (option) => {
    const session = sessions.find(
      (s) => s.id === option?.value && !option.disabled,
    );
    if (session) {
      onSelectSession(session);
      const coach = selectedProgram?.coaches.find(
        (c) => c.sessionId === session.id,
      );
      if (coach?.coachProduct) {
        onSelectProduct(session.product, coach.coachProduct);
      }
    }
  };

  const onSelectProgramOption: SelectFn<SelectOption> = (option) => {
    const program = programs.find(
      (p) => p.programId === option?.value && !option.disabled,
    );
    if (program) {
      onSelectProgramId(program.programId);
      const { session, coachProduct } = findFirstAvailableSession([program]);

      if (session) {
        onSelectSession(session);
        if (coachProduct) {
          onSelectProduct(session.product, coachProduct);
        }
      } else {
        onSelectSession(null);
        onSelectProduct(null, null);
      }
    }
  };

  const getBookingTypeClassName = (active: boolean) => {
    const baseClasses =
      "p-2 rounded-lg border-2 transition-colors flex justify-center items-center w-full cursor-pointer";
    const activeClasses = "border-blue-500 bg-blue-50 text-blue-600";
    const inactiveClasses = "border-gray-300 hover:border-gray-400";

    return `${baseClasses} ${active ? activeClasses : inactiveClasses}`;
  };

  return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <CardTitle>How would you like to book?</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-center font-medium ml-1">
          <div
            className={getBookingTypeClassName(tab === "program")}
            onClick={() => onTabChange("program")}
          >
            Book within program
          </div>
          <div
            className={getBookingTypeClassName(tab === "independent")}
            onClick={() => onTabChange("independent")}
          >
            Book Independently
          </div>
        </div>

        {tab === "independent" && (
          <div className="flex items-start gap-3 rounded-lg bg-amber-50 p-4">
            <Lightbulb className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-700 font-normal">
              We recommend booking within a program. Sessions booked
              independently will not count towards program progress.
            </p>
          </div>
        )}

        {tab === "program" && (
          <>
            <div className="flex flex-col gap-2">
              <div className="font-semibold text-sm">Program</div>
              <Select
                value={programOptions.find(
                  (o) => o.value === selectedProgram?.programId,
                )}
                onChange={onSelectProgramOption}
                isSearchable={true}
                options={programOptions}
                isOptionDisabled={(option) => !!option.disabled}
                styles={CUSTOM_SELECT_STYLES}
                placeholder="Select a coaching program..."
                className="ml-1 font-normal"
              />
            </div>
            <div className="flex flex-col gap-2">
              <div className="font-semibold text-sm">Session</div>
              <div className="ml-1 flex flex-col gap-4">
                <Select
                  value={
                    selectedSession
                      ? sessionOptions.find(
                          (o) => o.value === selectedSession.id,
                        )
                      : null
                  }
                  onChange={onSelectSessionOption}
                  isSearchable={true}
                  options={sessionOptions}
                  isOptionDisabled={(option) => !!option.disabled}
                  styles={CUSTOM_SELECT_STYLES}
                  placeholder="Select a session..."
                  className="font-normal"
                />
                {selectedSession && (
                  <div className="rounded-lg border border-gray-300 bg-gray-200 p-4 space-y-1">
                    <div className="text-sm">
                      <span className="font-bold">Coaching Type:</span>{" "}
                      <span className="font-normal">
                        {selectedProduct?.specialty?.name}
                      </span>
                    </div>
                    <div className="text-sm">
                      <span className="font-bold">Length:</span>{" "}
                      <span className="font-normal">
                        {selectedProduct?.introProduct
                          ? selectedProduct.name
                          : ""}{" "}
                        {selectedProduct?.duration} minutes
                      </span>
                    </div>
                    <div className="text-sm">
                      <span className="font-bold">Scheduling Window:</span>{" "}
                      <span className="font-normal">
                        {formatDate(
                          selectedSession?.targetStartDate,
                          "d MMM yyyy",
                        )}{" "}
                        -{" "}
                        {formatDate(
                          selectedSession?.targetEndDate,
                          "d MMM yyyy",
                        )}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
